/**
 * SiteDetail page — unified cross-data view of a Baker Distributing site.
 *
 * Shows: site info, WAN uplink health, Meraki network devices, subnets,
 * circuits + bandwidth, hardware assets, employees, spend summary,
 * contracts, and branch rankings.
 *
 * Requirements: 32.4, 32.5, 32.10, 32.11
 */

import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { siteService } from '@/services/siteService';
import type {
  SiteDetailResponse,
  MerakiDeviceSummary,
  UplinkStatusSummary,
  SubnetEntry,
  SiteSpendSummary,
} from '@/services/siteService';
import { StatusBadge, type BadgeVariant } from '@/components/ui/StatusBadge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/types';

const UPLINK_VARIANTS: Record<string, BadgeVariant> = {
  active: 'green',
  ready: 'blue',
  connecting: 'yellow',
  'not connected': 'gray',
  failed: 'red',
};

const DEVICE_STATUS_VARIANTS: Record<string, BadgeVariant> = {
  online: 'green',
  offline: 'red',
  alerting: 'yellow',
  dormant: 'gray',
};

const fmt = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 0 });
const fmtCurrency = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function SiteDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, hasAnyRole } = useAuth();
  const siteNumber = id ?? '';

  const canWrite = user?.isAdmin ||
    user?.roles.some((r) => ['Admin', 'IT Admin', 'Contract Specialist'].includes(r));
  const canSeeFinancials = hasAnyRole([UserRole.Admin, UserRole.Controller, UserRole.CFO, UserRole.Accounting, UserRole.ContractSpecialist]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['site', siteNumber],
    queryFn: () => siteService.getByNumber(siteNumber),
    enabled: !!siteNumber,
  });

  if (!siteNumber) return <ErrorMessage message="Invalid site number." />;
  if (isLoading) return <LoadingSpinner message="Loading site details…" />;
  if (error || !data) {
    return (
      <ErrorMessage
        message={(error as Error)?.message ?? 'Failed to load site.'}
        onRetry={() => refetch()}
      />
    );
  }

  const {
    site,
    circuits,
    assets,
    employeeHeadcount,
    employeeCategoryBreakdown,
    employees,
    merakiDevices,
    uplinkStatus,
    subnets,
    spendSummary,
  } = data as SiteDetailResponse;

  const deviceCount = (assets as unknown[]).length;
  const deviceToEmployeeRatio =
    employeeHeadcount > 0 ? (deviceCount / employeeHeadcount).toFixed(2) : '—';

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-1">
          <Link to="/sites" className="text-slate-500 no-underline text-sm hover:text-baker-red">
            ← Sites
          </Link>
          {canWrite && (
            <div className="flex gap-2">
              <button
                onClick={() => navigate(`/sites/${siteNumber}/edit`)}
                className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 cursor-pointer hover:bg-page transition-all"
              >
                Edit Site
              </button>
            </div>
          )}
        </div>
        <h1 className="text-lg font-semibold text-text-primary">
          Site {site.siteNumber} — {site.branchName}
        </h1>
        <div className="flex gap-2 mt-2 items-center flex-wrap">
          <StatusBadge
            label={site.isActive ? 'Active' : 'Inactive'}
            variant={site.isActive ? 'green' : 'red'}
          />
          {uplinkStatus && (
            <StatusBadge
              label={uplinkStatus.hasRedundancy ? 'WAN Redundant' : 'Single WAN'}
              variant={uplinkStatus.hasRedundancy ? 'green' : 'yellow'}
            />
          )}
        </div>
      </div>

      {/* Top KPI Cards — clickable, scroll to section */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <KPICard label="Employees" value={fmt(employees?.length ?? employeeHeadcount)} sub={employeeHeadcount > 0 ? undefined : 'from payroll'} onClick={() => document.getElementById('section-employees')?.scrollIntoView({ behavior: 'smooth' })} />
        <KPICard label="Hardware Assets" value={fmt(deviceCount)} onClick={() => document.getElementById('section-assets')?.scrollIntoView({ behavior: 'smooth' })} />
        <KPICard label="Meraki Devices" value={fmt(merakiDevices?.total ?? 0)} sub={merakiDevices?.total ? `${merakiDevices.online} online` : undefined} onClick={() => {
          if (merakiDevices?.networkId) {
            window.open(`https://dashboard.meraki.com/o/715461/manage/nodes/show/${merakiDevices.networkId}`, '_blank');
          } else {
            document.getElementById('section-meraki')?.scrollIntoView({ behavior: 'smooth' });
          }
        }} />
        <KPICard label="Circuits" value={fmt((circuits as unknown[]).length)} sub={canSeeFinancials && spendSummary ? `${fmtCurrency(spendSummary.circuitMRC)}/mo` : undefined} onClick={() => document.getElementById('section-circuits')?.scrollIntoView({ behavior: 'smooth' })} />
        {canSeeFinancials && <KPICard label={`${new Date().getFullYear()} Spend`} value={fmtCurrency(spendSummary?.totalSpend ?? 0)} sub={spendSummary ? `${fmt(spendSummary.invoiceCount)} invoices` : undefined} onClick={() => document.getElementById('section-spend')?.scrollIntoView({ behavior: 'smooth' })} />}
        <KPICard label="Device:Employee" value={deviceToEmployeeRatio} onClick={() => document.getElementById('section-employees')?.scrollIntoView({ behavior: 'smooth' })} />
      </div>

      {/* Site Information */}
      <Section title="Site Information">
        <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3 text-sm">
          <Field label="Site Number" value={site.siteNumber} />
          <Field label="Branch Name" value={site.branchName} />
          <Field label="Address" value={site.address} />
          <Field label="City" value={site.city} />
          <Field label="State" value={site.state} />
          <Field label="Division" value={site.divisionName ?? site.divisionCode} />
          <Field label="Region" value={site.regionCode} />
          <Field label="Regional Manager" value={site.regionName} />
          <Field
            label="Lease Start"
            value={(site as unknown as Record<string, unknown>).leaseStartDate ? new Date(String((site as unknown as Record<string, unknown>).leaseStartDate)).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : null}
          />
          <Field
            label="Lease Expiration"
            value={site.leaseExpirationDate ? new Date(site.leaseExpirationDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : null}
          />
        </div>
      </Section>

      {/* WAN Uplink Status */}
      {uplinkStatus && <UplinkSection uplink={uplinkStatus} />}

      {/* Meraki Network Devices */}
      {merakiDevices && merakiDevices.total > 0 && (
        <div id="section-meraki">
          <MerakiSection meraki={merakiDevices} />
        </div>
      )}

      {/* Subnets / VLANs */}
      {subnets && subnets.length > 0 && <SubnetSection subnets={subnets} />}

      {/* Spend Summary */}
      {canSeeFinancials && spendSummary && (spendSummary.totalSpend > 0 || spendSummary.circuitMRC > 0) && (
        <div id="section-spend">
          <SpendSection spend={spendSummary} />
        </div>
      )}

      {/* Employees */}
      <div id="section-employees">
        <Section title={`Employees (${employees?.length ?? employeeHeadcount})`}>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3 text-sm mb-4">
            <Field label="Headcount" value={String(employees?.length ?? employeeHeadcount)} />
            <Field label="Hardware Assets" value={String(deviceCount)} />
            <Field label="Device-to-Employee Ratio" value={deviceToEmployeeRatio} />
          </div>
          {employeeCategoryBreakdown && employeeCategoryBreakdown.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-slate-600 mb-2">Category Breakdown</h3>
              <div className="flex flex-wrap gap-2">
                {employeeCategoryBreakdown.map((cb) => (
                  <span key={cb.category} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                    {cb.category} <span className="font-bold">{cb.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {employees && employees.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="border-b-2 border-border">
                    <Th>Name</Th>
                    <Th>Title</Th>
                    <Th>Email</Th>
                    <Th>Category</Th>
                  </tr>
                </thead>
                <tbody>
                  {employees.slice(0, 100).map((emp, i) => {
                    // Show category header when it changes
                    const prevCategory = i > 0 ? employees[i - 1].employeeCategory : null;
                    const showHeader = emp.employeeCategory !== prevCategory;
                    return (
                      <>
                        {showHeader && (
                          <tr key={`cat-${emp.employeeCategory}`}>
                            <td colSpan={4} className="pt-3 pb-1 px-2 text-xs font-semibold text-slate-500 uppercase tracking-wide bg-page/50">
                              {emp.employeeCategory ?? 'Uncategorized'}
                            </td>
                          </tr>
                        )}
                        <tr key={emp.employeeId ?? i} className="border-b border-border-subtle">
                          <Td>
                            <Link to={`/employees/${emp.employeeId}`} className="text-baker-red no-underline hover:underline font-medium">
                              {emp.legalName}
                            </Link>
                          </Td>
                          <Td>{emp.businessTitle ?? emp.jobTitle ?? '—'}</Td>
                          <Td>
                            {emp.workEmail ? (
                              <a href={`mailto:${emp.workEmail}`} className="text-baker-red no-underline hover:underline">
                                {emp.workEmail}
                              </a>
                            ) : '—'}
                          </Td>
                          <Td>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                              {emp.employeeCategory ?? '—'}
                            </span>
                          </Td>
                        </tr>
                      </>
                    );
                  })}
                </tbody>
              </table>
              {employees.length > 100 && (
                <p className="text-slate-500 text-xs mt-2">Showing first 100 of {employees.length} employees.</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No employees assigned to this site in the payroll system.</p>
          )}
        </Section>
      </div>

      {/* Data Circuits */}
      <div id="section-circuits">
      <Section title={`Data Circuits (${(circuits as unknown[]).length})`}>
        {(circuits as unknown[]).length === 0 ? (
          <p className="text-sm text-slate-400">No circuits associated with this site.</p>
        ) : (
          <div className="space-y-3">
            {(circuits as Record<string, unknown>[]).map((c) => {
              const carrier = String(c.CarrierVendor ?? c.carrierVendor ?? '—');
              const vendorId = c.CarrierVendorId ?? c.carrierVendorId;
              const circuitId = String(c.CircuitIdentifier ?? c.circuitIdentifier ?? '');
              const accountNum = String(c.AccountNumber ?? c.accountNumber ?? '');
              const accessType = String(c.AccessType ?? c.accessType ?? '—');
              const bandwidth = Number(c.BandwidthMbps ?? c.bandwidthMbps) || 0;
              const mrc = Number(c.MRC ?? c.mrc) || 0;
              const notes = String(c.Notes ?? c.notes ?? '');
              // Extract service description from notes (after last | )
              const noteParts = notes.split('|').map((s: string) => s.trim());
              const serviceDesc = noteParts.length > 2 ? noteParts.slice(2).join(' · ') : null;

              return (
                <div key={String(c.CircuitId ?? c.circuitId)} className="bg-page rounded-lg p-4 border border-border-subtle">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex items-center gap-3">
                      {vendorId ? (
                        <Link to={`/vendors/${vendorId}`} className="text-baker-red no-underline hover:underline font-semibold text-sm">
                          {carrier}
                        </Link>
                      ) : (
                        <span className="font-semibold text-sm text-text-primary">{carrier}</span>
                      )}
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">{accessType}</span>
                      {bandwidth > 0 && <span className="text-xs font-medium text-slate-600">{bandwidth} Mbps</span>}
                    </div>
                    <div className="text-right">
                      {canSeeFinancials && mrc > 0 && <span className="text-sm font-bold text-text-primary">{fmtCurrency(mrc)}/mo</span>}
                    </div>
                  </div>
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-x-6 gap-y-1 text-[13px]">
                    {circuitId && (
                      <div>
                        <span className="text-slate-500">Circuit ID:</span>{' '}
                        <span className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border border-border">{circuitId}</span>
                      </div>
                    )}
                    {accountNum && (
                      <div>
                        <span className="text-slate-500">Account #:</span>{' '}
                        <span className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border border-border">{accountNum}</span>
                      </div>
                    )}
                    {serviceDesc && (
                      <div>
                        <span className="text-slate-500">Service:</span>{' '}
                        <span className="text-slate-700">{serviceDesc}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {/* Total MRC */}
            {canSeeFinancials && (
            <div className="flex justify-end pt-2 border-t border-border">
              <span className="text-sm text-slate-500 mr-3">Total MRC:</span>
              <span className="text-sm font-bold text-text-primary">
                {fmtCurrency((circuits as Record<string, unknown>[]).reduce((sum, c) => sum + (Number(c.MRC ?? c.mrc) || 0), 0))}/mo
              </span>
            </div>
            )}
          </div>
        )}
      </Section>
      </div>

      {/* Hardware Assets */}
      <div id="section-assets">

      {/* Recent TDX Tickets */}
      <SiteTicketsSection siteNumber={siteNumber} />
      <Section title={`Hardware Assets (${deviceCount})`}>
        {deviceCount === 0 ? (
          <p className="text-sm text-slate-400">No hardware assets at this site.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="border-b-2 border-border">
                  <Th>Device Name</Th>
                  <Th>Serial</Th>
                  <Th>Model</Th>
                  <Th>Status</Th>
                  <Th>Assigned User</Th>
                </tr>
              </thead>
              <tbody>
                {(assets as Record<string, unknown>[]).slice(0, 50).map((a) => (
                  <tr
                    key={String(a.AssetId ?? a.assetId)}
                    className="border-b border-border-subtle"
                  >
                    <Td>
                      <Link
                        to={`/assets/${a.AssetId ?? a.assetId}`}
                        className="text-baker-red no-underline hover:underline"
                      >
                        {String(a.DeviceName ?? a.deviceName ?? '—')}
                      </Link>
                    </Td>
                    <Td>{String(a.SerialNumber ?? a.serialNumber ?? '—')}</Td>
                    <Td>{String(a.Model ?? a.model ?? '—')}</Td>
                    <Td>
                      <StatusBadge
                        label={String(a.LifecycleStatus ?? a.lifecycleStatus ?? '—')}
                        variant="gray"
                      />
                    </Td>
                    <Td>{String(a.AssignedUser ?? a.assignedUser ?? '—')}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
            {deviceCount > 50 && (
              <p className="text-slate-500 text-xs mt-2">
                Showing first 50 of {deviceCount} assets.
              </p>
            )}
          </div>
        )}
      </Section>
      </div>

    </div>
  );
}


// ─── Sub-components ────────────────────────────────────────────────────

function SiteTicketsSection({ siteNumber }: { siteNumber: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['site-tickets', siteNumber],
    queryFn: async () => {
      try {
        const res = await fetch(`/api/sites/${siteNumber}/tickets`, { credentials: 'include' });
        if (!res.ok) return { tickets: [] };
        return res.json();
      } catch { return { tickets: [] }; }
    },
    retry: false,
  });

  if (isLoading) return <Section title="Recent Support Tickets"><p className="text-sm text-slate-400">Loading tickets...</p></Section>;
  if (!data?.tickets?.length) return null;

  return (
    <Section title={`Recent Support Tickets (${data.tickets.length})`}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr className="border-b-2 border-border">
              <th className="text-left py-1.5 px-2 font-medium text-slate-600">ID</th>
              <th className="text-left py-1.5 px-2 font-medium text-slate-600">Title</th>
              <th className="text-left py-1.5 px-2 font-medium text-slate-600">Requestor</th>
              <th className="text-left py-1.5 px-2 font-medium text-slate-600">Status</th>
              <th className="text-left py-1.5 px-2 font-medium text-slate-600">Created</th>
            </tr>
          </thead>
          <tbody>
            {data.tickets.map((t: Record<string, unknown>) => (
              <tr key={String(t.ticketId)} className="border-b border-border-subtle">
                <td className="py-1.5 px-2">
                  <a href={String(t.url)} target="_blank" rel="noopener noreferrer" className="text-baker-red no-underline hover:underline font-mono text-xs">
                    {String(t.ticketId)}
                  </a>
                </td>
                <td className="py-1.5 px-2">{String(t.title ?? '—')}</td>
                <td className="py-1.5 px-2 text-slate-600">{String(t.requestor ?? '—')}</td>
                <td className="py-1.5 px-2">
                  <StatusBadge label={String(t.status ?? '—')} variant={String(t.status) === 'Closed' ? 'gray' : String(t.status) === 'New' ? 'blue' : 'green'} />
                </td>
                <td className="py-1.5 px-2 text-slate-500">{t.createdDate ? new Date(String(t.createdDate)).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function KPICard({ label, value, sub, onClick }: { label: string; value: string; sub?: string; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      className="bg-white rounded-lg border border-border p-4 text-left cursor-pointer hover:border-baker-accent/40 hover:shadow-sm transition-all w-full"
    >
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-bold text-text-primary mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </button>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6 bg-white rounded-lg border border-border p-5">
      <h2 className="text-base font-semibold text-text-primary mb-3">{title}</h2>
      {children}
    </section>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <span className="text-slate-500">{label}:</span> {value ?? '—'}
    </div>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className="text-slate-600 font-medium py-1.5 px-2"
      style={{ textAlign: align ?? 'left' }}
    >
      {children}
    </th>
  );
}

function Td({ children, align }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <td className="py-1.5 px-2" style={{ textAlign: align ?? 'left' }}>
      {children}
    </td>
  );
}

// ─── WAN Uplink Section ────────────────────────────────────────────────

function UplinkSection({ uplink }: { uplink: UplinkStatusSummary }) {
  return (
    <Section title="WAN Uplink Status">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* WAN1 */}
        <div className="bg-page rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-slate-700">WAN 1 (Primary)</span>
            <StatusBadge
              label={uplink.wan1Status ?? 'unknown'}
              variant={UPLINK_VARIANTS[uplink.wan1Status ?? ''] ?? 'gray'}
            />
          </div>
          <div className="space-y-1 text-[13px]">
            <div><span className="text-slate-500">Provider:</span> {uplink.wan1Provider ?? '—'}</div>
            <div><span className="text-slate-500">Public IP:</span> {uplink.wan1PublicIp ?? '—'}</div>
          </div>
        </div>

        {/* WAN2 */}
        <div className="bg-page rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-slate-700">WAN 2 (Backup)</span>
            <StatusBadge
              label={uplink.wan2Status ?? 'not connected'}
              variant={UPLINK_VARIANTS[uplink.wan2Status ?? ''] ?? 'gray'}
            />
          </div>
          <div className="space-y-1 text-[13px]">
            <div><span className="text-slate-500">Provider:</span> {uplink.wan2Provider ?? '—'}</div>
            <div><span className="text-slate-500">Public IP:</span> {uplink.wan2PublicIp ?? '—'}</div>
          </div>
        </div>

        {/* Cellular */}
        <div className="bg-page rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-slate-700">Cellular</span>
            <StatusBadge
              label={uplink.cellularStatus ?? 'not connected'}
              variant={UPLINK_VARIANTS[uplink.cellularStatus ?? ''] ?? 'gray'}
            />
          </div>
          <div className="space-y-1 text-[13px]">
            <div><span className="text-slate-500">Provider:</span> {uplink.cellularProvider ?? '—'}</div>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ─── Meraki Devices Section ────────────────────────────────────────────

function MerakiSection({ meraki }: { meraki: MerakiDeviceSummary }) {
  const dashboardUrl = meraki.networkId
    ? `https://dashboard.meraki.com/o/715461/manage/nodes/show/${meraki.networkId}`
    : null;

  return (
    <section className="mb-6 bg-white rounded-lg border border-border p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-text-primary">Meraki Network Devices ({meraki.total})</h2>
        {dashboardUrl && (
          <a
            href={dashboardUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-white text-xs font-medium text-slate-600 no-underline hover:bg-page hover:border-baker-accent/40 transition-all"
          >
            Open in Meraki Dashboard ↗
          </a>
        )}
      </div>
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-page rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-emerald-600">{meraki.online}</div>
          <div className="text-xs text-slate-500">Online</div>
        </div>
        <div className="bg-page rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-red-600">{meraki.offline}</div>
          <div className="text-xs text-slate-500">Offline</div>
        </div>
        <div className="bg-page rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-amber-600">{meraki.alerting}</div>
          <div className="text-xs text-slate-500">Alerting</div>
        </div>
        <div className="bg-page rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-text-primary">{meraki.total}</div>
          <div className="text-xs text-slate-500">Total</div>
        </div>
      </div>

      {/* By product type */}
      {meraki.byProductType.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {meraki.byProductType.map((pt) => (
            <span
              key={pt.productType}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700"
            >
              {pt.productType}
              <span className="font-bold">{pt.count}</span>
            </span>
          ))}
        </div>
      )}

      {/* Device table */}
      {meraki.devices.length > 0 && (
        <details>
          <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600 mb-2">
            Show all {meraki.total} devices
          </summary>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="border-b-2 border-border">
                  <Th>Name</Th>
                  <Th>Model</Th>
                  <Th>Type</Th>
                  <Th>Status</Th>
                  <Th>LAN IP</Th>
                  <Th>Public IP</Th>
                  <Th>Last Seen</Th>
                </tr>
              </thead>
              <tbody>
                {(meraki.devices as Record<string, unknown>[]).slice(0, 100).map((d) => (
                  <tr
                    key={String(d.Serial ?? d.serial)}
                    className="border-b border-border-subtle"
                  >
                    <Td>{String(d.Name ?? d.name ?? '—')}</Td>
                    <Td>{String(d.Model ?? d.model ?? '—')}</Td>
                    <Td>{String(d.ProductType ?? d.productType ?? '—')}</Td>
                    <Td>
                      <StatusBadge
                        label={String(d.Status ?? d.status ?? '—')}
                        variant={
                          DEVICE_STATUS_VARIANTS[String(d.Status ?? d.status ?? '')] ?? 'gray'
                        }
                      />
                    </Td>
                    <Td>
                      <span className="font-mono text-xs">
                        {String(d.LanIp ?? d.lanIp ?? '—')}
                      </span>
                    </Td>
                    <Td>
                      <span className="font-mono text-xs">
                        {String(d.PublicIp ?? d.publicIp ?? '—')}
                      </span>
                    </Td>
                    <Td>
                      {d.LastReportedAt ?? d.lastReportedAt
                        ? new Date(String(d.LastReportedAt ?? d.lastReportedAt)).toLocaleString()
                        : '—'}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </section>
  );
}

// ─── Subnet Section ────────────────────────────────────────────────────

function SubnetSection({ subnets }: { subnets: SubnetEntry[] }) {
  return (
    <Section title={`Network Subnets / VLANs (${subnets.length})`}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr className="border-b-2 border-border">
              <Th>VLAN ID</Th>
              <Th>Subnet Range</Th>
              <Th>Network / Purpose</Th>
            </tr>
          </thead>
          <tbody>
            {subnets.map((s, i) => (
              <tr key={i} className="border-b border-border-subtle">
                <Td>
                  <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">
                    {s.vlanId ?? '—'}
                  </span>
                </Td>
                <Td>
                  <span className="font-mono text-xs">{s.subnetRange}</span>
                </Td>
                <Td>{s.networkName ?? '—'}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ─── Spend Summary Section ─────────────────────────────────────────────

function SpendSection({ spend }: { spend: SiteSpendSummary }) {
  return (
    <Section title={`${new Date().getFullYear()} Spend Summary`}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-page rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-text-primary">
            {fmtCurrency(spend.totalSpend)}
          </div>
          <div className="text-xs text-slate-500 mt-1">{new Date().getFullYear()} Invoiced Spend</div>
          <div className="text-xs text-slate-400">{fmt(spend.invoiceCount)} invoices</div>
        </div>
        <div className="bg-page rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-text-primary">
            {fmtCurrency(spend.circuitMRC)}
          </div>
          <div className="text-xs text-slate-500 mt-1">Monthly Circuit Cost</div>
        </div>
        <div className="bg-page rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-text-primary">
            {fmtCurrency(spend.circuitMRC * 12)}
          </div>
          <div className="text-xs text-slate-500 mt-1">Annualized Circuit Cost</div>
        </div>
      </div>

      {/* Top vendors */}
      {spend.topVendors.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-600 mb-2">Top Vendors by Spend</h3>
          <div className="space-y-2">
            {spend.topVendors.map((v) => {
              const pct =
                spend.totalSpend > 0
                  ? ((v.totalAmount / spend.totalSpend) * 100).toFixed(1)
                  : '0';
              return (
                <div key={v.vendorName} className="flex items-center gap-3">
                  <span className="text-sm text-slate-700 w-40 truncate">{v.vendorName}</span>
                  <div className="flex-1 h-2 bg-page rounded-full overflow-hidden">
                    <div
                      className="h-full bg-baker-accent/70 rounded-full"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-500 w-24 text-right">
                    {fmtCurrency(v.totalAmount)}
                  </span>
                  <span className="text-xs text-slate-400 w-12 text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Section>
  );
}
